import { components, paths } from "@/api-schema";
import {
  DataExportType,
  PathsV1CompanySearchPostParametersQueryExtend,
  PathsV1CompanySearchPostParametersQueryOrderByDirection,
  PathsV1CompanySearchPostParametersQueryOrderByField,
} from "@/api-schema-enums";
import { DEFAULT_PAGE_SIZE } from "@/common/api/_defaults";
import { useCompanyDeleteMutation } from "@/common/api/company/use-company-delete.mutation";
import { useCompanyRestoreMutation } from "@/common/api/company/use-company-restore.mutation";
import { useCompanySearchQuery } from "@/common/api/company/use-company-search.query";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { TableFeatures } from "@/components/data-table/types";
import { CompanyGroupFilter } from "@/components/filter/predefined/company-group-filter";
import { CompanyTypeFilter } from "@/components/filter/predefined/company-type-filter";
import { DeletedStatus, DeletedStatusFilter } from "@/components/filter/predefined/deleted-status-filter";
import i18next from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { createBasePredefinedTable } from "./_components/base-predefined-table";
import {
  BasePredefinedTableProps,
  DataFetchHooks,
  PredefinedTableConfig,
} from "./_components/base-predefined-table.types";
import { companyTableDefaultColumns } from "./company-table-columns";

type Company = components["schemas"]["Company"] & RowWithIdentifier;
type CompanySorting = NonNullable<paths["/v1/company/search"]["post"]["parameters"]["query"]["orderBy"]>;
type CompanyQueryParams = paths["/v1/company/search"]["post"]["parameters"]["query"];
type CompanyRequestBody = paths["/v1/company/search"]["post"]["requestBody"]["content"]["application/json"];
type CompanyExtend = NonNullable<paths["/v1/company/search"]["post"]["parameters"]["query"]["extend"]>;

const DEFAULT_EXTEND: CompanyExtend = [
  PathsV1CompanySearchPostParametersQueryExtend.CompanyIdentifiers,
  PathsV1CompanySearchPostParametersQueryExtend.CompanyGroup,
] as const;

const DEFAULT_COLUMNS = [
  companyTableDefaultColumns.columnMobileSummary,
  companyTableDefaultColumns.columnName,
  companyTableDefaultColumns.columnType,
  companyTableDefaultColumns.columnCompanyGroup,
  companyTableDefaultColumns.columnSapPrefix,
  companyTableDefaultColumns.columnVatId,
  companyTableDefaultColumns.columnV1Id,
  companyTableDefaultColumns.columnTechnicalId,
  companyTableDefaultColumns.columnDeletedAt,
];

const COMPANY_TABLE_CONFIG: PredefinedTableConfig<CompanySorting> = {
  defaultPageSize: DEFAULT_PAGE_SIZE,
  defaultSorting: [
    {
      direction: PathsV1CompanySearchPostParametersQueryOrderByDirection.Asc,
      field: PathsV1CompanySearchPostParametersQueryOrderByField.Name,
    },
  ] as const,
  defaultTableConfig: {
    searchPlaceholder: i18next.t("common:SearchCompanies"),
    features: {
      multiSelection: false,
      pagination: true,
      search: true,
      expansion: false,
    } as TableFeatures,
  } as const,
  defaultRowActions: {
    showEdit: true,
    showDelete: true,
    showView: true,
  } as const,
  sortingMapping: {
    name: ["name"],
    companyGroup: [PathsV1CompanySearchPostParametersQueryOrderByField.CompanyGroup],
    technicalId: ["technicalId"],
    sapPrefix: ["sapPrefix"],
    commercialRegisterCourt: ["commercialRegisterCourt"],
    commercialRegisterNumber: ["commercialRegisterNumber"],
    vatId: ["vatId"],
    deletedAt: ["deletedAt"],
    createdAt: ["createdAt"],
    updatedAt: ["updatedAt"],
  } as const,
  exportType: DataExportType.Company,
} as const;

const COMPANY_TABLE_QUERY_HOOKS: DataFetchHooks<Company, CompanyQueryParams, CompanyRequestBody> = {
  useSearchQuery: useCompanySearchQuery,
  useDeleteMutation: useCompanyDeleteMutation,
  useRestoreMutation: useCompanyRestoreMutation,
} as const;

export interface CompanyTableProps extends BasePredefinedTableProps<
  Company,
  CompanySorting,
  CompanyQueryParams,
  CompanyRequestBody,
  CompanyExtend[number]
> {
  extend?: CompanyExtend;
}

const BaseCompanyTable = createBasePredefinedTable({
  config: COMPANY_TABLE_CONFIG,
  queryHooks: COMPANY_TABLE_QUERY_HOOKS,
  defaultColumns: DEFAULT_COLUMNS,
});

export function CompanyTable(props: CompanyTableProps) {
  const { t } = useTranslation(["common"]);
  const {
    name = "company-table",
    extend = DEFAULT_EXTEND,
    showCreateButton = true,
    showExportButton = true,
    body = { deletedAt: [DeletedStatus.ACTIVE, DeletedStatus.DELETED] },
    filters,
    filterValues,
    columns,
    ...restProps
  } = props;

  const defaultFilters = useMemo(
    () => filters ?? [new CompanyGroupFilter(), new CompanyTypeFilter(), new DeletedStatusFilter()],
    [filters],
  );
  const defaultFilterValues = useMemo(() => filterValues ?? { deletedAt: [DeletedStatus.ACTIVE] }, [filterValues]);

  return (
    <BaseCompanyTable
      searchPlaceholder={t("common:SearchCompanies")}
      {...{
        name,
        extend,
        showCreateButton,
        showExportButton,
        body,
        columns,
        filters: defaultFilters,
        filterValues: defaultFilterValues,
        confirmActions: ["delete", "restore"],
        toastMessages: {
          deleteSuccess: t("CompanyHasBeenDeleted"),
          deleteError: t("DeleteCompanyError"),
          restoreSuccess: t("CompanyHasBeenRestored"),
          restoreError: t("RestoreCompanyError"),
        },
        confirmLabels: {
          delete: (row: Company) => ({
            title: row.deletedAt ? t("DeleteCompanyPermanently") : t("DeleteCompany"),
            text: row.deletedAt ? t("DeleteCompanyPermanentlyDescription") : t("DeleteCompanyDescription"),
            confirmButton: row.deletedAt ? t("YesDeletePermanently") : t("ram:YesDelete"),
            cancelButton: t("Cancel"),
          }),
          restore: {
            title: t("RestoreCompany"),
            text: t("RestoreCompanyDescription"),
            confirmButton: t("YesRestore"),
            cancelButton: t("Cancel"),
          },
        },
        ...restProps,
      }}
    />
  );
}
